<template>
  <form @submit.prevent="submit">
    <!-- ── 1. Dados gerais ── -->
    <h3 class="mdn-Heading mdn-Heading--xxs mb-3">Dados gerais</h3>

    <div class="mb-3">
      <SGPFormInputComponent
        type="text"
        name="name"
        v-model="formulario.name"
        placeholder="Nome do e-mail"
        data-vv-as="Nome"
        v-validate="'required|min:3'"
        :error="validationErrors.first('name')"
      />
    </div>

    <div class="mb-3">
      <SGPFormInputComponent
        name="description"
        type="textarea"
        rows="2"
        placeholder="Descrição"
        data-vv-as="Descrição"
        v-model="formulario.description"
        :error="validationErrors.first('description')"
      />
    </div>

    <div class="mb-3">
      <label class="v-label theme--light d-block mb-1" style="font-size:12px">
        Evento que dispara o envio
      </label>

      <!-- Criação: editável -->
      <template v-if="tipoFormulario === 'salvar'">
        <SGPFormInputComponent
          type="text"
          name="event_key"
          v-model="formulario.event_key"
          placeholder="ex: sla.alerta"
          data-vv-as="Evento"
          v-validate="'required'"
          :error="validationErrors.first('event_key')"
        />
        <small class="text-muted d-block mt-1">
          Chave do evento em código que vai disparar este e-mail.
        </small>
      </template>

      <!-- Edição: somente leitura -->
      <template v-else>
        <code class="px-2 py-1 rounded" style="background:#f4f4f4;font-size:13px">
          {{ formulario.event_key || '—' }}
        </code>
        <small class="text-muted d-block mt-1">Evento definido em código — não editável.</small>
      </template>
    </div>

    <div class="mb-4">
      <SGPFormToggleComponent
        name="is_active"
        :titulo="formulario.is_active ? 'Ativo' : 'Inativo'"
        v-model="formulario.is_active"
      />
      <small class="text-muted d-block">
        Quando inativo, o envio do e-mail fica suspenso até que seja reativado.
      </small>
    </div>

    <v-divider class="mb-4" />

    <!-- ── 2. Editor de e-mail ── -->
    <h3 class="mdn-Heading mdn-Heading--xxs mb-3">Editor de e-mail</h3>

    <div class="mb-3">
      <SGPFormInputComponent
        type="text"
        name="subject"
        v-model="formulario.subject"
        placeholder="Assunto do e-mail (suporta variáveis)"
        data-vv-as="Assunto"
        v-validate="'required|min:3'"
        :error="validationErrors.first('subject')"
      />
    </div>

    <div class="email-editor-layout">
      <!-- Editor HTML -->
      <div class="email-editor-body">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <label class="v-label theme--light mb-0" style="font-size:12px">
            Corpo do e-mail (HTML)
          </label>
          <div class="preview-toggle">
            <button
              type="button"
              :class="['preview-tab', { active: !modoPreview }]"
              @click="modoPreview = false"
            >Editar</button>
            <button
              type="button"
              :class="['preview-tab', { active: modoPreview }]"
              @click="modoPreview = true"
            >Preview</button>
          </div>
        </div>

        <textarea
          v-if="!modoPreview"
          ref="editorBody"
          v-model="formulario.body"
          class="body-editor-textarea"
          placeholder="Cole ou edite o HTML do e-mail aqui..."
          rows="18"
          spellcheck="false"
        />

        <iframe
          v-else
          class="body-preview-frame"
          :srcdoc="formulario.body"
          sandbox="allow-same-origin"
          frameborder="0"
        />
      </div>

      <!-- Painel de variáveis -->
      <div class="email-editor-variaveis">
        <p class="text-muted mb-2" style="font-size:12px;font-weight:600">
          VARIÁVEIS DISPONÍVEIS
        </p>

        <!-- Aviso: evento sem variáveis no backend -->
        <div v-if="semVariaveisDoServidor && formulario.event_key" class="variaveis-aviso-evento mb-2">
          <SGPIconComponent icon="mdi-alert-circle-outline" class="mr-1" />
          <span>
            Nenhuma variável disponível. O evento
            <code>{{ formulario.event_key }}</code>
            ainda não foi implementado no backend.
          </span>
        </div>

        <template v-if="variaveisExibidas.length">
          <button
            v-for="v in variaveisExibidas"
            :key="v.key"
            type="button"
            :class="['variavel-btn', { 'variavel-btn--nova': v.nova }]"
            :title="v.description || (v.nova ? 'Detectada no editor' : '')"
            @click="inserirVariavel(v.key)"
          >
            <code>{{ rotuloDaVariavel(v.key) }}</code>
            <span class="variavel-label">
              {{ v.label || v.key }}
              <em v-if="v.nova" class="variavel-nova-badge">nova</em>
            </span>
          </button>
          <small class="text-muted d-block mt-2">
            Clique para inserir no cursor do editor.
          </small>
        </template>

        <p v-else class="text-muted" style="font-size:12px">
          Digite <code>{{ rotuloDaVariavel('chave') }}</code> no corpo do e-mail
          para incluir variáveis que serão substituídas no momento do envio.
        </p>
      </div>
    </div>

    <div class="container-botoes mt-4">
      <button
        type="submit"
        class="v-btn v-btn--has-bg v-btn--rounded theme--light v-size--default primary text-capitalize"
        :disabled="salvandoTemplate"
      >
        {{ tipoFormulario === 'salvar' ? 'Salvar' : 'Atualizar' }}
      </button>
      <button
        type="button"
        class="v-btn v-btn--outlined v-btn--rounded theme--light v-size--default grey-5--text text-capitalize"
        @click="fecharModal"
      >
        Cancelar
      </button>
    </div>
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const formularioVazio = () => ({
  id: null,
  name: '',
  description: '',
  event_key: '',
  subject: '',
  body: '',
  is_active: true
})

export default {
  name: 'FormularioEmailAutomaticoComponent',

  components: {},

  props: {
    tipoFormulario: {
      type: String,
      default: 'editar'
    },
    template: {
      type: Object,
      default: null
    },
    availableVariables: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      formulario: this.montarFormulario(this.template),
      modoPreview: false
    }
  },

  computed: {
    ...mapGetters('gestaoEmailsAutomaticos', ['salvandoTemplate']),

    semVariaveisDoServidor () {
      return !this.availableVariables || this.availableVariables.length === 0
    },

    variaveisExibidas () {
      const REGEX = /\{\{(\w+)\}\}/g
      const body = this.formulario.body || ''
      const keysNoBody = new Set()
      let m
      while ((m = REGEX.exec(body)) !== null) {
        keysNoBody.add(m[1])
      }

      // Variáveis do servidor — apenas scope 'body' ou 'both' (sem scope = body por compat)
      const mapa = {}
      ;(this.availableVariables || []).forEach(v => {
        const scope = v.scope || 'body'
        if (scope === 'body' || scope === 'both') {
          mapa[v.key] = { ...v, nova: false }
        }
      })

      // Variáveis detectadas no body mas não no servidor
      keysNoBody.forEach(key => {
        if (!mapa[key]) {
          mapa[key] = { key, label: key, description: '', nova: true }
        }
      })

      return Object.values(mapa)
    }
  },

  watch: {
    template (novo) {
      this.formulario = this.montarFormulario(novo)
    }
  },

  methods: {
    ...mapActions('gestaoEmailsAutomaticos', [
      'salvarTemplate',
      'atualizarTemplate'
    ]),

    montarFormulario (template) {
      const base = formularioVazio()
      if (!template) return base
      return {
        ...base,
        id: template.id ?? null,
        name: template.name ?? '',
        description: template.description ?? '',
        event_key: template.event_key ?? '',
        subject: template.subject ?? '',
        body: template.body ?? '',
        is_active: template.is_active !== false
      }
    },

    montarPayload () {
      const payload = {
        name: this.formulario.name,
        description: this.formulario.description,
        subject: this.formulario.subject,
        body: this.formulario.body,
        is_active: !!this.formulario.is_active
      }
      if (this.tipoFormulario === 'salvar') {
        payload.event_key = this.formulario.event_key
      }
      if (this.formulario.id) payload.id = this.formulario.id
      return payload
    },

    inserirVariavel (key) {
      const el = this.$refs.editorBody
      if (!el) return
      const inicio = el.selectionStart ?? el.value.length
      const fim = el.selectionEnd ?? inicio
      const texto = `{{${key}}}`
      this.formulario.body =
        el.value.slice(0, inicio) + texto + el.value.slice(fim)
      this.$nextTick(() => {
        el.focus()
        el.selectionStart = inicio + texto.length
        el.selectionEnd = inicio + texto.length
      })
    },

    rotuloDaVariavel (key) {
      return `{{${key}}}`
    },

    submit () {
      this.$validator.validateAll().then(async (valido) => {
        if (!valido) return
        const payload = this.montarPayload()
        let resp
        if (this.tipoFormulario === 'salvar') {
          resp = await this.salvarTemplate(payload)
          if (resp) {
            this.$emit('criado', resp.id)
            this.fecharModal()
          }
        } else {
          resp = await this.atualizarTemplate(payload)
          if (resp) this.fecharModal()
        }
      })
    },

    fecharModal () {
      this.$emit('fechar')
    }
  }
}
</script>

<style scoped>
.email-editor-layout {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.email-editor-body {
  flex: 1;
  min-width: 0;
}

.preview-toggle {
  display: flex;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

.preview-tab {
  padding: 2px 12px;
  font-size: 12px;
  background: #fff;
  border: none;
  cursor: pointer;
  color: #555;
}

.preview-tab.active {
  background: var(--v-primary-base, #1976d2);
  color: #fff;
}

.body-editor-textarea {
  width: 100%;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  resize: vertical;
  background: #fafafa;
  color: #222;
}

.body-editor-textarea:focus {
  outline: none;
  border-color: var(--v-primary-base, #1976d2);
  background: #fff;
}

.body-preview-frame {
  width: 100%;
  min-height: 480px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
}

.email-editor-variaveis {
  width: 220px;
  flex-shrink: 0;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  max-height: 480px;
  overflow-y: auto;
}

.variavel-btn {
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: border-color 0.15s;
}

.variavel-btn:hover {
  border-color: var(--v-primary-base, #1976d2);
}

.variavel-btn--nova {
  border-style: dashed;
  border-color: #aaa;
  background: #f5f5f5;
}

.variavel-btn--nova:hover {
  border-color: var(--v-primary-base, #1976d2);
}

.variavel-nova-badge {
  font-style: normal;
  font-size: 10px;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 3px;
  padding: 1px 4px;
  margin-left: 4px;
  vertical-align: middle;
}

.variavel-btn code {
  font-size: 11px;
  color: #c0392b;
}

.variavel-label {
  font-size: 11px;
  color: #555;
  margin-top: 2px;
}

.variaveis-aviso-evento {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 11px;
  color: #e65100;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 4px;
  padding: 6px 8px;
  line-height: 1.4;
}

.variaveis-aviso-evento code {
  font-size: 11px;
  color: #bf360c;
  background: transparent;
}
</style>
